import { META_GRAPH_API_VERSION } from 'lib/meta-capi';

const WHATSAPP_SESSION_HOURS = 24;

export type MetaWhatsAppTemplateComponent = {
  type: string;
  text?: string;
  format?: string;
  example?: {
    body_text?: string[][];
    header_text?: string[];
  };
};

export type MetaWhatsAppTemplate = {
  id: string;
  name: string;
  status: string;
  language: string;
  category: string;
  components?: MetaWhatsAppTemplateComponent[];
  rejected_reason?: string | null;
};

export type WhatsAppTemplateCatalogRow = {
  meta_template_id: string | null;
  meta_template_name: string;
  language: string;
  category: string;
  status: string;
  body_preview: string | null;
  variable_count: number;
  variable_labels: Array<{ index: string; label: string }>;
  components: MetaWhatsAppTemplateComponent[] | null;
  last_synced_at: string;
};

type MetaRequestResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; error: string };

function getWhatsAppConfig() {
  return {
    wabaId: process.env.NEXT_META_WHATSAPP_BUSINESS_ACCOUNT_ID?.trim() || '',
    accessToken:
      process.env.NEXT_META_WHATSAPP_ACCESS_TOKEN?.trim() ||
      process.env.NEXT_FACEBOOK_CONVERSIONS_ACCESS_TOKEN?.trim() ||
      '',
  };
}

export function isMetaWhatsAppConfigured() {
  const { wabaId, accessToken } = getWhatsAppConfig();
  return Boolean(wabaId && accessToken);
}

async function metaWhatsAppRequest<T>(
  path: string,
  init?: RequestInit,
): Promise<MetaRequestResult<T>> {
  const { accessToken } = getWhatsAppConfig();

  if (!accessToken) {
    return {
      ok: false,
      status: 503,
      error: 'Meta WhatsApp access token is not configured.',
    };
  }

  const url = `https://graph.facebook.com/${META_GRAPH_API_VERSION}${path}`;
  const separator = path.includes('?') ? '&' : '?';
  const authedUrl = `${url}${separator}access_token=${encodeURIComponent(accessToken)}`;

  const response = await fetch(authedUrl, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {}),
    },
  });

  const text = await response.text();
  let body: unknown = null;

  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!response.ok) {
    const errorMessage =
      typeof body === 'object' &&
      body &&
      'error' in body &&
      typeof (body as { error?: { message?: string } }).error?.message ===
        'string'
        ? (body as { error: { message: string } }).error.message
        : typeof body === 'string'
          ? body
          : JSON.stringify(body);

    return {
      ok: false,
      status: response.status,
      error: errorMessage || `Meta request failed (${response.status})`,
    };
  }

  return { ok: true, data: body as T };
}

export function extractTemplateVariableLabels(text?: string | null) {
  if (!text) {
    return [];
  }

  const positionalMatches = text.match(/\{\{\d+\}\}/g) || [];

  if (positionalMatches.length > 0) {
    const indexes = Array.from(
      new Set(positionalMatches.map((match) => match.replace(/\D/g, ''))),
    ).sort((left, right) => Number(left) - Number(right));

    return indexes.map((index) => ({
      index,
      label: `Variable ${index}`,
    }));
  }

  const namedMatches = text.match(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g) || [];
  const names = Array.from(
    new Set(namedMatches.map((match) => match.slice(2, -2))),
  );

  return names.map((name) => ({
    index: name,
    label: name.replace(/_/g, ' '),
  }));
}

export function countTemplateVariables(text?: string | null) {
  return extractTemplateVariableLabels(text).length;
}

export function extractBodyPreview(
  components?: MetaWhatsAppTemplateComponent[],
) {
  const body = components?.find((component) => component.type === 'BODY');
  return body?.text || null;
}

export function extractHeaderText(
  components?: MetaWhatsAppTemplateComponent[] | null,
) {
  const header = components?.find((component) => component.type === 'HEADER');
  return header?.text || '';
}

export function extractFooterText(
  components?: MetaWhatsAppTemplateComponent[] | null,
) {
  const footer = components?.find((component) => component.type === 'FOOTER');
  return footer?.text || '';
}

export function defaultVariableLabels(count: number) {
  return Array.from({ length: count }, (_, index) => ({
    index: String(index + 1),
    label: `Variable ${index + 1}`,
  }));
}

export function buildTemplateComponents(input: {
  body: string;
  header?: string;
  footer?: string;
  examples?: string[];
}): MetaWhatsAppTemplateComponent[] {
  const components: MetaWhatsAppTemplateComponent[] = [];

  if (input.header?.trim()) {
    components.push({
      type: 'HEADER',
      format: 'TEXT',
      text: input.header.trim(),
    });
  }

  const bodyComponent: MetaWhatsAppTemplateComponent = {
    type: 'BODY',
    text: input.body.trim(),
  };
  const variableLabels = extractTemplateVariableLabels(input.body);

  if (variableLabels.length > 0 && input.examples?.length) {
    bodyComponent.example = {
      body_text: [input.examples.slice(0, variableLabels.length)],
    };
  }

  components.push(bodyComponent);

  if (input.footer?.trim()) {
    components.push({
      type: 'FOOTER',
      text: input.footer.trim(),
    });
  }

  return components;
}

export function catalogRowFromTemplateParts(input: {
  metaTemplateId?: string | null;
  name: string;
  language: string;
  category: string;
  status: string;
  components: MetaWhatsAppTemplateComponent[];
}): WhatsAppTemplateCatalogRow {
  const bodyPreview = extractBodyPreview(input.components);
  const variableLabels = extractTemplateVariableLabels(bodyPreview);

  return {
    meta_template_id: input.metaTemplateId || null,
    meta_template_name: input.name,
    language: input.language,
    category: input.category.toUpperCase(),
    status: input.status.toUpperCase(),
    body_preview: bodyPreview,
    variable_count: variableLabels.length,
    variable_labels: variableLabels,
    components: input.components,
    last_synced_at: new Date().toISOString(),
  };
}

export function mapMetaTemplateToCatalogRow(
  template: MetaWhatsAppTemplate,
): WhatsAppTemplateCatalogRow {
  return catalogRowFromTemplateParts({
    metaTemplateId: template.id,
    name: template.name,
    language: template.language,
    category: template.category?.toUpperCase() || 'UTILITY',
    status: template.status?.toUpperCase() || 'PENDING',
    components: template.components || [],
  });
}

export async function listMetaWhatsAppTemplates(limit = 100) {
  const { wabaId } = getWhatsAppConfig();

  if (!wabaId) {
    return {
      ok: false as const,
      status: 503,
      error: 'NEXT_META_WHATSAPP_BUSINESS_ACCOUNT_ID is not configured.',
    };
  }

  const fields = [
    'id',
    'name',
    'status',
    'language',
    'category',
    'components',
    'rejected_reason',
  ].join(',');

  return metaWhatsAppRequest<{ data?: MetaWhatsAppTemplate[] }>(
    `/${wabaId}/message_templates?limit=${limit}&fields=${fields}`,
  );
}

export type CreateMetaWhatsAppTemplateInput = {
  name: string;
  language: string;
  category: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION';
  body: string;
  header?: string;
  footer?: string;
  examples?: string[];
};

export async function createMetaWhatsAppTemplate(
  input: CreateMetaWhatsAppTemplateInput,
) {
  const { wabaId } = getWhatsAppConfig();

  if (!wabaId) {
    return {
      ok: false as const,
      status: 503,
      error: 'NEXT_META_WHATSAPP_BUSINESS_ACCOUNT_ID is not configured.',
    };
  }

  const components = buildTemplateComponents(input);

  return metaWhatsAppRequest<{ id?: string; status?: string }>(
    `/${wabaId}/message_templates`,
    {
      method: 'POST',
      body: JSON.stringify({
        name: input.name.trim(),
        language: input.language.trim(),
        category: input.category,
        components,
      }),
    },
  );
}

export type UpdateMetaWhatsAppTemplateInput = {
  category?: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION';
  body: string;
  header?: string;
  footer?: string;
  examples?: string[];
};

export async function getMetaWhatsAppTemplate(templateId: string) {
  return metaWhatsAppRequest<MetaWhatsAppTemplate>(`/${templateId}`);
}

export async function updateMetaWhatsAppTemplate(
  templateId: string,
  input: UpdateMetaWhatsAppTemplateInput,
) {
  const components = buildTemplateComponents(input);

  return metaWhatsAppRequest<{
    success?: boolean;
    id?: string;
    status?: string;
  }>(`/${templateId}`, {
    method: 'POST',
    body: JSON.stringify({
      category: input.category,
      components,
    }),
  });
}

export async function deleteMetaWhatsAppTemplate(input: {
  templateId: string;
  name: string;
}) {
  const { wabaId } = getWhatsAppConfig();

  if (!wabaId) {
    return {
      ok: false as const,
      status: 503,
      error: 'NEXT_META_WHATSAPP_BUSINESS_ACCOUNT_ID is not configured.',
    };
  }

  const params = new URLSearchParams({
    hsm_id: input.templateId,
    name: input.name,
  });

  return metaWhatsAppRequest<{ success?: boolean }>(
    `/${wabaId}/message_templates?${params.toString()}`,
    {
      method: 'DELETE',
    },
  );
}

export function getWhatsAppSessionWindowMs() {
  return WHATSAPP_SESSION_HOURS * 60 * 60 * 1000;
}
